import { onBeforeUnmount, onMounted, type Ref } from 'vue'
import { useAiraWidget } from '../store/useAiraWidgetStore'
import { AIRA_PROPERTY } from './useAira'

export interface AiraTouchGuardOptions {
  /** Grabs within the window that trip the scolding. */
  threshold?: number
  /** Dragging this far over the element (px, cumulative) also trips it. */
  dragThreshold?: number
  /** Sliding window the grabs and the drag are counted in, ms. */
  window?: number
  /** Minimum gap between two scoldings, ms. */
  cooldown?: number
  /** How long she hovers the element while scolding, ms. */
  dwell?: number
  /** Texts, used in order and then repeated — escalation reads better than random. */
  lines?: string[]
  /** Scolding number that ends it: she says `finalLine` and throws the visitor out. */
  finaleAt?: number
  finalLine?: string
}

const DEFAULTS = {
  threshold: 4,
  dragThreshold: 1400,
  window: 12000,
  cooldown: 15000,
  dwell: 3800,
}

/**
 * Watches an element for repeated pointer play and, past a threshold, sends Aira over
 * to complain about it.
 *
 * Listening happens on `document` in the capture phase and the element is matched via
 * `composedPath()`. The rail's runtime owns pointer events on its own node and calls
 * `setPointerCapture`, so a listener bound to that node would miss the moves once a
 * drag starts — and anything it stops from propagating never reaches a bubble-phase
 * listener at all.
 */
export function useAiraTouchGuard(
  elementRef: Ref<HTMLElement | null> | (() => HTMLElement | null),
  options: AiraTouchGuardOptions = {},
) {
  const store = useAiraWidget()

  const threshold = options.threshold ?? DEFAULTS.threshold
  const dragThreshold = options.dragThreshold ?? DEFAULTS.dragThreshold
  const windowMs = options.window ?? DEFAULTS.window
  const cooldown = options.cooldown ?? DEFAULTS.cooldown
  const dwell = options.dwell ?? DEFAULTS.dwell

  let grabs: number[] = []
  let dragDistance = 0
  let dragResetAt = 0
  let isPressed = false
  let lastX = 0
  let lastY = 0
  let lastScoldAt = 0
  let lineIndex = 0
  let scoldCount = 0
  let gazeFrame = 0
  let timers: ReturnType<typeof setTimeout>[] = []

  // Resolved per event, never cached: the rail mounts asynchronously and a node
  // captured at mount time can be stale or missing.
  const resolve = () =>
    typeof elementRef === 'function' ? elementRef() : elementRef.value

  const isOverElement = (event: Event) => {
    const element = resolve()
    if (!element) return false
    return event.composedPath().includes(element)
  }

  const after = (ms: number, fn: () => void) => {
    timers.push(setTimeout(fn, ms))
  }

  const followWithEyes = (target: HTMLElement) => {
    const rect = target.getBoundingClientRect()
    store.lookAt({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 })
    gazeFrame = requestAnimationFrame(() => followWithEyes(target))
  }

  const stopFollowing = () => {
    if (gazeFrame) cancelAnimationFrame(gazeFrame)
    gazeFrame = 0
    store.lookAtCursor()
  }

  const land = () => {
    // Whatever was grabbed while she was hovering does not count — the next scolding
    // has to be earned from scratch, otherwise she fires again the moment the
    // cooldown lapses.
    grabs = []
    dragDistance = 0
    dragResetAt = Date.now()

    store.write(AIRA_PROPERTY.angry, false)
    store.write(AIRA_PROPERTY.tracking, true)
    store.stopShaking()
    stopFollowing()
    store.flyHome()
    store.clearHint()
  }

  /**
   * The finale: she walks into the camera — centre of the viewport, eyes forward,
   * growing — and the screen gives out.
   */
  const walkIntoCamera = () => {
    stopFollowing()
    store.clearHint()
    store.flyTo({ x: window.innerWidth / 2, y: window.innerHeight / 2 })
    store.approach()

    // Matches the approach transition in AiraFloatingWidget.
    after(2600, () => {
      store.showBlackout()
      store.endApproach()
      land()
    })
  }

  const scold = (target: HTMLElement) => {
    const rect = target.getBoundingClientRect()
    scoldCount += 1
    const isFinale = options.finaleAt !== undefined && scoldCount >= options.finaleAt

    // Park above the element's centre — the rail is wide, and its right edge would
    // put her out in the margin instead of over the clothes.
    store.flyTo({ x: rect.left + rect.width / 2, y: rect.top + Math.min(rect.height * 0.3, 160) })
    followWithEyes(target)

    if (isFinale && options.finalLine) {
      store.say(options.finalLine)
    } else {
      const lines = options.lines ?? []
      if (lines.length) {
        store.say(lines[lineIndex % lines.length])
        lineIndex += 1
      }
    }

    // Tracking overwrites the expression's eye properties in the current artboard,
    // so it has to stand down while she is angry.
    store.write(AIRA_PROPERTY.tracking, false)
    store.write(AIRA_PROPERTY.angry, true)
    store.shake(isFinale ? 1500 : 1100)

    if (isFinale) {
      scoldCount = 0
      lineIndex = 0
      after(1600, walkIntoCamera)
      return
    }

    after(dwell, land)
  }

  /** Trip the scolding once either counter passes its threshold. */
  const check = () => {
    const now = Date.now()
    grabs = grabs.filter((time) => now - time < windowMs)

    const tripped = grabs.length >= threshold || dragDistance >= dragThreshold
    if (!tripped) return
    if (now - lastScoldAt < cooldown) return

    const target = resolve()
    if (!target) return

    grabs = []
    dragDistance = 0
    lastScoldAt = now
    scold(target)
  }

  const onPointerDown = (event: PointerEvent) => {
    if (!isOverElement(event)) return
    isPressed = true
    lastX = event.clientX
    lastY = event.clientY
    grabs.push(Date.now())
    if (import.meta.env.DEV) {
      console.debug('[aira-guard] grab', grabs.length, 'drag', Math.round(dragDistance))
    }
    check()
  }

  const onPointerMove = (event: PointerEvent) => {
    if (!isPressed) return

    const now = Date.now()
    // The drag total decays with the same window as the grabs, otherwise a slow
    // browse over several minutes would eventually trip it.
    if (now - dragResetAt > windowMs) {
      dragDistance = 0
      dragResetAt = now
    }

    dragDistance += Math.hypot(event.clientX - lastX, event.clientY - lastY)
    lastX = event.clientX
    lastY = event.clientY
    check()
  }

  const onPointerUp = () => {
    isPressed = false
  }

  onMounted(() => {
    document.addEventListener('pointerdown', onPointerDown, { capture: true, passive: true })
    document.addEventListener('pointermove', onPointerMove, { capture: true, passive: true })
    document.addEventListener('pointerup', onPointerUp, { capture: true, passive: true })
    document.addEventListener('pointercancel', onPointerUp, { capture: true, passive: true })
  })

  onBeforeUnmount(() => {
    document.removeEventListener('pointerdown', onPointerDown, { capture: true })
    document.removeEventListener('pointermove', onPointerMove, { capture: true })
    document.removeEventListener('pointerup', onPointerUp, { capture: true })
    document.removeEventListener('pointercancel', onPointerUp, { capture: true })
    for (const timer of timers) clearTimeout(timer)
    timers = []
    stopFollowing()
    store.stopShaking()
  })
}
